import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import type { Node, Edge } from '@xyflow/react';
import { ProcessingStepType } from '../../types';
import type {
  NormalizationConfig,
  DeduplicationGroupingConfig,
  SuppressionConfig,
  EnrichmentConfig,
  CorrelationConfig,
  IncidentGateConfig,
  IncidentCreationConfig,
  NotificationStepConfig,
  FieldSuggestion,
} from '../../types/pipelineConfig';
import {
  getDefaultNormalizationConfig,
  getDefaultDeduplicationConfig,
  getDefaultSuppressionConfig,
  getDefaultEnrichmentConfig,
  getDefaultCorrelationConfig,
  getDefaultIncidentGateConfig,
  getDefaultIncidentCreationConfig,
  getDefaultNotificationConfig,
  getNormalizedFieldSuggestions,
} from '../../utils/pipelineDefaults';
import { NormalizationConfigEditor } from './NormalizationConfigEditor';
import { DeduplicationGroupingConfigEditor } from './DeduplicationGroupingConfigEditor';
import { SuppressionConfigEditor } from './SuppressionConfigEditor';
import { EnrichmentConfigEditor } from './EnrichmentConfigEditor';
import { CorrelationConfigEditor } from './CorrelationConfigEditor';
import { IncidentGateConfigEditor } from './IncidentGateConfigEditor';
import { IncidentCreationConfigEditor } from './IncidentCreationConfigEditor';
import { NotificationConfigEditor } from './NotificationConfigEditor';
import { useEscapeKey } from '../../hooks/useEscapeKey';

// --- Types ---

interface StepConfigPanelProps {
  nodeId: string;
  stepType: string;
  label: string;
  isEnabled: boolean;
  configuration: string | null;
  endpointType: string | number;
  tenantId?: string;
  customerId?: string;
  nodes: Node[];
  edges: Edge[];
  onSave: (nodeId: string, configuration: string, isEnabled: boolean) => void;
  onClose: () => void;
}

// --- Upstream Field Context ---

/**
 * Walk the DAG backwards from a node to find upstream Normalization/Enrichment configs.
 * Returns field suggestions based on what upstream steps output.
 */
function getUpstreamFields(nodeId: string, nodes: Node[], edges: Edge[]): FieldSuggestion[] {
  const visited = new Set<string>();
  const fields: FieldSuggestion[] = [];

  // BFS backwards through edges
  const queue = [nodeId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    if (visited.has(current)) continue;
    visited.add(current);

    // Find all edges pointing TO this node
    const incomingEdges = edges.filter(e => e.target === current);
    for (const edge of incomingEdges) {
      const sourceNode = nodes.find(n => n.id === edge.source);
      if (!sourceNode) continue;

      const nodeData = sourceNode.data as Record<string, unknown>;
      const stepType = nodeData.stepType as string | null;
      const config = nodeData.configuration as string | null;

      // Extract fields from Normalization outputs
      if (stepType === ProcessingStepType.Normalization && config) {
        try {
          const normConfig = JSON.parse(config) as NormalizationConfig;
          const mappings = normConfig.defaultNormalization?.rules || [];
          for (const rule of mappings) {
            if (rule.targetField) {
              const path = `normalized.${rule.targetField}`;
              if (!fields.some(f => f.path === path)) {
                fields.push({
                  path,
                  label: path,
                  description: `Normalized from ${rule.sourceField}`,
                  category: 'Normalization Output',
                });
              }
            }
          }
        } catch { /* ignore parse errors */ }
      }

      // Extract fields from Enrichment outputs
      if (stepType === ProcessingStepType.Enrichment && config) {
        try {
          const enrichConfig = JSON.parse(config) as EnrichmentConfig;
          const rules = enrichConfig.rules || [];
          for (const rule of rules) {
            if (rule.targetField) {
              const path = `normalized.${rule.targetField}`;
              if (!fields.some(f => f.path === path)) {
                fields.push({
                  path,
                  label: path,
                  description: `Enriched by ${rule.name || rule.sourceType}`,
                  category: 'Enrichment Output',
                });
              }
            }
          }
        } catch { /* ignore parse errors */ }
      }

      queue.push(edge.source);
    }
  }

  // Always include base normalized fields
  const baseSuggestions = getNormalizedFieldSuggestions();
  for (const s of baseSuggestions) {
    if (!fields.some(f => f.path === s.path)) {
      fields.push(s);
    }
  }

  return fields;
}

// --- Main Component ---

export function StepConfigPanel({
  nodeId,
  stepType,
  label,
  isEnabled: initialIsEnabled,
  configuration,
  endpointType,
  tenantId,
  customerId,
  nodes,
  edges,
  onSave,
  onClose,
}: StepConfigPanelProps) {
  useEscapeKey(onClose, true);

  const upstreamFields = useMemo(
    () => getUpstreamFields(nodeId, nodes, edges),
    [nodeId, nodes, edges],
  );

  const [stepEnabled, setStepEnabled] = useState(initialIsEnabled);
  const [prevInitialIsEnabled, setPrevInitialIsEnabled] = useState(initialIsEnabled);

  if (initialIsEnabled !== prevInitialIsEnabled) {
    setPrevInitialIsEnabled(initialIsEnabled);
    setStepEnabled(initialIsEnabled);
  }

  const parseConfig = <T,>(fallback: T): T => {
    if (!configuration) {
      return fallback;
    }

    try {
      const parsed = JSON.parse(configuration) as T | null;
      return parsed ?? fallback;
    } catch {
      return fallback;
    }
  };

  // Parse existing config or use defaults
  const [normConfig, setNormConfig] = useState<NormalizationConfig>(() => {
    if (configuration) {
      try { return JSON.parse(configuration) as NormalizationConfig; } catch { /* */ }
    }
    return getDefaultNormalizationConfig(endpointType);
  });

  const [dedupConfig, setDedupConfig] = useState<DeduplicationGroupingConfig>(() => {
    if (configuration) {
      try { return JSON.parse(configuration) as DeduplicationGroupingConfig; } catch { /* */ }
    }
    return getDefaultDeduplicationConfig(endpointType);
  });

  const [suppressionConfig, setSuppressionConfig] = useState<SuppressionConfig>(() => {
    if (configuration) {
      try { return JSON.parse(configuration) as SuppressionConfig; } catch { /* */ }
    }
    return getDefaultSuppressionConfig(endpointType);
  });

  const [enrichmentConfig, setEnrichmentConfig] = useState<EnrichmentConfig>(() => {
    if (configuration) {
      try { return JSON.parse(configuration) as EnrichmentConfig; } catch { /* */ }
    }
    return getDefaultEnrichmentConfig(endpointType);
  });

  const [correlationConfig, setCorrelationConfig] = useState<CorrelationConfig>(() => {
    if (configuration) {
      try { return JSON.parse(configuration) as CorrelationConfig; } catch { /* */ }
    }
    return getDefaultCorrelationConfig(endpointType);
  });

  const [gateConfig, setGateConfig] = useState<IncidentGateConfig>(() => {
    if (configuration) {
      try { return JSON.parse(configuration) as IncidentGateConfig; } catch { /* */ }
    }
    return getDefaultIncidentGateConfig(endpointType);
  });

  const [incidentConfig, setIncidentConfig] = useState<IncidentCreationConfig>(() => {
    if (configuration) {
      try { return JSON.parse(configuration) as IncidentCreationConfig; } catch { /* */ }
    }
    return getDefaultIncidentCreationConfig(endpointType);
  });

  const [notificationConfig, setNotificationConfig] = useState<NotificationStepConfig>(() => {
    if (configuration) {
      try { return JSON.parse(configuration) as NotificationStepConfig; } catch { /* */ }
    }
    return getDefaultNotificationConfig(endpointType);
  });

  const configSyncKey = `${stepType}|${String(endpointType)}|${configuration ?? ''}`;
  const [prevConfigSyncKey, setPrevConfigSyncKey] = useState(configSyncKey);

  if (configSyncKey !== prevConfigSyncKey) {
    setPrevConfigSyncKey(configSyncKey);
    switch (stepType) {
      case ProcessingStepType.Normalization:
        setNormConfig(parseConfig(getDefaultNormalizationConfig(endpointType)));
        break;
      case ProcessingStepType.Deduplication:
        setDedupConfig(parseConfig(getDefaultDeduplicationConfig(endpointType)));
        break;
      case ProcessingStepType.Suppression:
        setSuppressionConfig(parseConfig(getDefaultSuppressionConfig(endpointType)));
        break;
      case ProcessingStepType.Enrichment:
        setEnrichmentConfig(parseConfig(getDefaultEnrichmentConfig(endpointType)));
        break;
      case ProcessingStepType.Correlation:
        setCorrelationConfig(parseConfig(getDefaultCorrelationConfig(endpointType)));
        break;
      case ProcessingStepType.IncidentGate:
        setGateConfig(parseConfig(getDefaultIncidentGateConfig(endpointType)));
        break;
      case ProcessingStepType.IncidentCreation:
        setIncidentConfig(parseConfig(getDefaultIncidentCreationConfig(endpointType)));
        break;
      case ProcessingStepType.Notification:
        setNotificationConfig(parseConfig(getDefaultNotificationConfig(endpointType)));
        break;
      default:
        break;
    }
  }

  const handleSave = useCallback(() => {
    let configJson: string;
    switch (stepType) {
      case ProcessingStepType.Normalization:
        configJson = JSON.stringify(normConfig);
        break;
      case ProcessingStepType.Deduplication:
        configJson = JSON.stringify(dedupConfig);
        break;
      case ProcessingStepType.Suppression:
        configJson = JSON.stringify(suppressionConfig);
        break;
      case ProcessingStepType.Enrichment:
        configJson = JSON.stringify(enrichmentConfig);
        break;
      case ProcessingStepType.Correlation:
        configJson = JSON.stringify(correlationConfig);
        break;
      case ProcessingStepType.IncidentGate:
        configJson = JSON.stringify(gateConfig);
        break;
      case ProcessingStepType.IncidentCreation:
        configJson = JSON.stringify(incidentConfig);
        break;
      case ProcessingStepType.Notification:
        configJson = JSON.stringify(notificationConfig);
        break;
      default:
        configJson = configuration || '{}';
    }
    onSave(nodeId, configJson, stepEnabled);
  }, [stepType, nodeId, onSave, configuration, stepEnabled, normConfig, dedupConfig, suppressionConfig, enrichmentConfig, correlationConfig, gateConfig, incidentConfig, notificationConfig]);

  const getCurrentConfig = useCallback(() => {
    switch (stepType) {
      case ProcessingStepType.Normalization:
        return normConfig;
      case ProcessingStepType.Deduplication:
        return dedupConfig;
      case ProcessingStepType.Suppression:
        return suppressionConfig;
      case ProcessingStepType.Enrichment:
        return enrichmentConfig;
      case ProcessingStepType.Correlation:
        return correlationConfig;
      case ProcessingStepType.IncidentGate:
        return gateConfig;
      case ProcessingStepType.IncidentCreation:
        return incidentConfig;
      case ProcessingStepType.Notification:
        return notificationConfig;
      default:
        try {
          return configuration ? JSON.parse(configuration) : {};
        } catch {
          return {};
        }
    }
  }, [stepType, normConfig, dedupConfig, suppressionConfig, enrichmentConfig, correlationConfig, gateConfig, incidentConfig, notificationConfig, configuration]);

  const renderEditor = () => {
    const missingConfig = (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        <p className="text-sm">No configuration available</p>
      </div>
    );

    switch (stepType) {
      case ProcessingStepType.Normalization:
        if (!normConfig) return missingConfig;
        return (
          <NormalizationConfigEditor
            config={normConfig}
            onChange={setNormConfig}
            endpointType={endpointType}
            tenantId={tenantId}
            hideToggle
          />
        );
      case ProcessingStepType.Deduplication:
        if (!dedupConfig) return missingConfig;
        return (
          <DeduplicationGroupingConfigEditor
            config={dedupConfig}
            onChange={setDedupConfig}
            endpointType={endpointType}
            hideToggle
            upstreamFields={upstreamFields}
          />
        );
      case ProcessingStepType.Suppression:
        if (!suppressionConfig) return missingConfig;
        return (
          <SuppressionConfigEditor
            config={suppressionConfig}
            onChange={setSuppressionConfig}
            endpointType={endpointType}
            hideToggle
          />
        );
      case ProcessingStepType.Enrichment:
        if (!enrichmentConfig) return missingConfig;
        return (
          <EnrichmentConfigEditor
            config={enrichmentConfig}
            onChange={setEnrichmentConfig}
            endpointType={endpointType}
            tenantId={tenantId || ''}
            customerId={customerId}
            hideToggle
          />
        );
      case ProcessingStepType.Correlation:
        if (!correlationConfig) return missingConfig;
        return (
          <CorrelationConfigEditor
            config={correlationConfig}
            onChange={setCorrelationConfig}
            endpointType={endpointType}
            tenantId={tenantId}
            hideToggle
          />
        );
      case ProcessingStepType.IncidentGate:
        if (!gateConfig) return missingConfig;
        return (
          <IncidentGateConfigEditor
            config={gateConfig}
            onChange={setGateConfig}
            endpointType={endpointType}
            hideToggle
          />
        );
      case ProcessingStepType.IncidentCreation:
        if (!incidentConfig) return missingConfig;
        return (
          <IncidentCreationConfigEditor
            config={incidentConfig}
            onChange={setIncidentConfig}
            endpointType={endpointType}
            tenantId={tenantId || ''}
            hideToggle
          />
        );
      case ProcessingStepType.Notification:
        if (!notificationConfig) return missingConfig;
        return (
          <NotificationConfigEditor
            config={notificationConfig}
            onChange={setNotificationConfig}
            endpointType={endpointType}
            hideToggle
          />
        );
      default:
        return (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <p className="text-sm">No configuration editor available for this step type.</p>
            <p className="text-xs mt-1">Step type: {stepType}</p>
          </div>
        );
    }
  };

  // Resizable panel width — persisted in localStorage
  const STORAGE_KEY = 'pipeline-config-panel-width';
  const [panelWidth, setPanelWidth] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = parseInt(stored, 10);
      if (!isNaN(parsed) && parsed >= 480) return Math.min(parsed, window.innerWidth * 0.85);
    }
    return Math.max(480, Math.round(window.innerWidth * 0.3));
  });
  const isResizing = useRef(false);
  const MIN_WIDTH = 480;
  const MAX_WIDTH_RATIO = 0.85; // max 85% of viewport

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing.current) return;
      const newWidth = Math.max(MIN_WIDTH, Math.min(window.innerWidth * MAX_WIDTH_RATIO, window.innerWidth - e.clientX));
      setPanelWidth(newWidth);
    };
    const handleMouseUp = () => {
      if (!isResizing.current) return;
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Persist the final width
      setPanelWidth(w => { localStorage.setItem(STORAGE_KEY, String(Math.round(w))); return w; });
    };
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const startResize = useCallback(() => {
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  return (
    <div
      className="fixed inset-y-0 right-0 bg-white dark:bg-gray-800 shadow-2xl border-l border-gray-200 dark:border-gray-700 z-50 flex flex-col"
      style={{ width: panelWidth }}
    >
      {/* Resize handle */}
      <div
        className="absolute inset-y-0 left-0 w-2 cursor-col-resize hover:bg-primary-400/30 active:bg-primary-500/40 z-10 transition-colors"
        onMouseDown={startResize}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize configuration panel"
        tabIndex={0}
      />
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            Configure: {label}
          </h3>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {stepType} step • {upstreamFields.length} upstream fields available
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* View JSON button */}
          <button
            type="button"
            onClick={() => {
              const currentConfig = getCurrentConfig();
              const jsonWindow = window.open('', '_blank', 'width=800,height=600');
              if (jsonWindow) {
                jsonWindow.document.write(`
                  <!DOCTYPE html>
                  <html>
                  <head>
                    <title>${label} - Configuration JSON</title>
                    <style>
                      body { 
                        font-family: 'Monaco', 'Menlo', 'Consolas', monospace; 
                        margin: 20px; 
                        background: #1e1e1e; 
                        color: #d4d4d4; 
                      }
                      pre { 
                        background: #252526; 
                        padding: 20px; 
                        border-radius: 4px; 
                        overflow: auto; 
                        font-size: 13px; 
                        line-height: 1.5;
                      }
                      h1 { 
                        color: #569cd6; 
                        font-size: 18px; 
                        margin-bottom: 10px; 
                      }
                    </style>
                  </head>
                  <body>
                    <h1>${label} - Configuration JSON</h1>
                    <pre>${JSON.stringify(currentConfig, null, 2)}</pre>
                  </body>
                  </html>
                `);
                jsonWindow.document.close();
              }
            }}
            className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="View configuration JSON"
            title="View raw JSON configuration"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
            </svg>
          </button>
          {/* Close button */}
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close configuration panel"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Enable/Disable toggle */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
            stepEnabled
              ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
          }`}>
            {stepEnabled ? 'Enabled' : 'Disabled'}
          </span>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {stepEnabled ? 'Step will execute in the pipeline' : 'Step will be skipped during execution'}
          </span>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={stepEnabled}
          aria-label={`${stepEnabled ? 'Disable' : 'Enable'} ${label} step`}
          onClick={() => setStepEnabled(!stepEnabled)}
          className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
            stepEnabled ? 'bg-primary-700' : 'bg-gray-300 dark:bg-gray-600'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
              stepEnabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>

      {/* Upstream fields hint */}
      {upstreamFields.length > 0 && (
        <details className="px-5 py-2 border-b border-gray-100 dark:border-gray-700/50 text-xs">
          <summary className="cursor-pointer text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200">
            Available fields from upstream steps ({upstreamFields.length})
          </summary>
          <div className="mt-2 max-h-32 overflow-y-auto space-y-1">
            {upstreamFields.slice(0, 20).map(f => (
              <div key={f.path} className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                <code className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded font-mono">{f.path}</code>
                {f.description && <span className="text-gray-400 truncate">{f.description}</span>}
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Editor content */}
      <div className="flex-1 overflow-y-auto p-5">
        {renderEditor()}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/80">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSave}
          className="px-4 py-2 text-sm font-medium text-white bg-primary-700 rounded-lg hover:bg-primary-800 shadow-sm"
        >
          Apply Configuration
        </button>
      </div>
    </div>
  );
}
